import type { QueryClient, QueryKey } from "@tanstack/query-core";

/**
 * `contracts/client-api.md` § Retry and backoff, `spec.md` § Edge cases: a 429 waits
 * `Retry-After` exactly rather than backing off, pauses the affected query family for that
 * duration, and surfaces `RateLimitNotice` in place rather than as a toast. The transport
 * does the exact wait; this module holds the pauses and the notices the surfaces read.
 */
export interface RateLimitPause {
  readonly family: QueryKey;
  readonly until: number;
}

export interface RateLimits {
  /** Record a 429 for the family a request belongs to; returns the pause. */
  pause(family: QueryKey, retryAfterMs: number, now?: number): RateLimitPause;
  /** The active pause covering a key, or null; expired pauses are dropped on read. */
  pauseFor(key: QueryKey, now?: number): RateLimitPause | null;
  /** True while `key` is inside a paused family, which the query defaults read as `enabled: false`. */
  isPaused(key: QueryKey, now?: number): boolean;
  subscribe(listener: () => void): () => void;
  /** Resume every family whose pause has expired, refetching the queries it held. */
  resumeExpired(queryClient: QueryClient, now?: number): void;
}

export function isPrefix(prefix: QueryKey, key: QueryKey): boolean {
  return prefix.length <= key.length && prefix.every((part, i) => JSON.stringify(part) === JSON.stringify(key[i]));
}

export function createRateLimits(clock: () => number = Date.now): RateLimits {
  const pauses: RateLimitPause[] = [];
  const listeners = new Set<() => void>();
  const notify = () => {
    for (const listener of listeners) listener();
  };
  const prune = (now: number) => {
    let changed = false;
    for (let i = pauses.length - 1; i >= 0; i -= 1) {
      if ((pauses[i]?.until ?? 0) <= now) {
        pauses.splice(i, 1);
        changed = true;
      }
    }
    return changed;
  };
  return {
    pause(family, retryAfterMs, now = clock()) {
      const pause = { family, until: now + retryAfterMs };
      const existing = pauses.findIndex((p) => isPrefix(p.family, family) && isPrefix(family, p.family));
      if (existing >= 0) pauses[existing] = pause;
      else pauses.push(pause);
      notify();
      return pause;
    },
    pauseFor(key, now = clock()) {
      prune(now);
      return pauses.find((p) => isPrefix(p.family, key)) ?? null;
    },
    isPaused(key, now = clock()) {
      prune(now);
      return pauses.some((p) => isPrefix(p.family, key));
    },
    subscribe(listener) {
      listeners.add(listener);
      return () => void listeners.delete(listener);
    },
    resumeExpired(queryClient, now = clock()) {
      const expired = pauses.filter((p) => p.until <= now);
      if (prune(now)) {
        notify();
        for (const pause of expired) void queryClient.invalidateQueries({ queryKey: pause.family });
      }
    },
  };
}
